// ChesiPortal_API (same service as ecommerce /cms).
// Priority:
// 1) REACT_APP_API_URL from build env
// 2) Railway production API in production builds
// 3) localhost for local development
const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://chesiwms-production-1eb5.up.railway.app/api'
    : 'http://localhost:5000/api');

class ApiService {
  constructor() {
    this.token = localStorage.getItem('token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    return headers;
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          this.setToken(null);
          window.location.href = '/login';
        }
        throw new Error(data.message || 'Request failed');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  async requestFormData(endpoint, method, formData) {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    try {
      const response = await fetch(url, { method, headers, body: formData });
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 401) {
          this.setToken(null);
          window.location.href = '/login';
        }
        throw new Error(data.message || 'Request failed');
      }
      return data;
    } catch (error) {
      throw error;
    }
  }

  // Auth endpoints
  async login(username, password) {
    const data = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    if (data.success && data.data.token) {
      this.setToken(data.data.token);
    }
    return data;
  }

  async register(userData) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async getMe() {
    return this.request('/auth/me');
  }

  // CMS endpoints
  async getPages(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/cms/pages${queryParams ? `?${queryParams}` : ''}`);
  }

  async getPageById(id) {
    return this.request(`/cms/pages/${id}`);
  }

  async getPageBySlug(slug) {
    return this.request(`/cms/pages/${slug}`);
  }

  async createPage(pageData) {
    return this.request('/cms/pages', {
      method: 'POST',
      body: JSON.stringify(pageData),
    });
  }

  async updatePage(id, pageData) {
    return this.request(`/cms/pages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(pageData),
    });
  }

  async deletePage(id) {
    return this.request(`/cms/pages/${id}`, {
      method: 'DELETE',
    });
  }

  async getContentBlocks(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/cms/content-blocks${queryParams ? `?${queryParams}` : ''}`);
  }

  async getContentBlockById(id) {
    return this.request(`/cms/content-blocks/${id}`);
  }

  // Ecommerce (CMS) endpoints
  async getEcommerceBrands(params = {}) {
    const q = new URLSearchParams(params).toString();
    return this.request(`/ecommerce/brands${q ? `?${q}` : ''}`);
  }

  async getEcommerceBrandById(id) {
    return this.request(`/ecommerce/brands/${id}`);
  }

  async createEcommerceBrand(data, logoFile = null) {
    if (logoFile) {
      const form = new FormData();
      Object.keys(data).forEach(k => { if (data[k] !== undefined) form.append(k, data[k]); });
      form.append('logo', logoFile);
      return this.requestFormData('/ecommerce/brands', 'POST', form);
    }
    return this.request('/ecommerce/brands', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateEcommerceBrand(id, data, logoFile = null) {
    if (logoFile) {
      const form = new FormData();
      Object.keys(data).forEach(k => { if (data[k] !== undefined) form.append(k, data[k]); });
      form.append('logo', logoFile);
      return this.requestFormData(`/ecommerce/brands/${id}`, 'PUT', form);
    }
    return this.request(`/ecommerce/brands/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteEcommerceBrand(id) {
    return this.request(`/ecommerce/brands/${id}`, { method: 'DELETE' });
  }

  async getEcommerceCategories(params = {}) {
    const q = new URLSearchParams(params).toString();
    return this.request(`/ecommerce/categories${q ? `?${q}` : ''}`);
  }

  async getEcommerceCategoryTree() {
    return this.request('/ecommerce/categories/tree');
  }

  async getEcommerceCategoryById(id) {
    return this.request(`/ecommerce/categories/${id}`);
  }

  async createEcommerceCategory(data) {
    return this.request('/ecommerce/categories', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateEcommerceCategory(id, data) {
    return this.request(`/ecommerce/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteEcommerceCategory(id) {
    return this.request(`/ecommerce/categories/${id}`, { method: 'DELETE' });
  }

  async getEcommerceProducts(params = {}) {
    const q = new URLSearchParams(params).toString();
    return this.request(`/ecommerce/products${q ? `?${q}` : ''}`);
  }

  async getEcommerceProductById(id) {
    return this.request(`/ecommerce/products/${id}`);
  }

  async createEcommerceProduct(formData) {
    return this.requestFormData('/ecommerce/products', 'POST', formData);
  }

  async updateEcommerceProduct(id, formData) {
    return this.requestFormData(`/ecommerce/products/${id}`, 'PUT', formData);
  }

  async setEcommerceProductPrimaryImage(productId, imageId) {
    return this.request(`/ecommerce/products/${productId}/primary-image/${imageId}`, { method: 'PUT' });
  }

  async deleteEcommerceProductImage(productId, imageId) {
    return this.request(`/ecommerce/products/${productId}/image/${imageId}`, { method: 'DELETE' });
  }

  async archiveEcommerceProduct(id) {
    return this.request(`/ecommerce/products/${id}/archive`, { method: 'DELETE' });
  }

  async deleteEcommerceProduct(id) {
    return this.request(`/ecommerce/products/${id}`, { method: 'DELETE' });
  }

  async getEcommerceOrders(params = {}) {
    const q = new URLSearchParams(params).toString();
    return this.request(`/ecommerce/orders${q ? `?${q}` : ''}`);
  }

  async getEcommerceOrderById(id) {
    return this.request(`/ecommerce/orders/${id}`);
  }

  async createContentBlock(blockData) {
    return this.request('/cms/content-blocks', {
      method: 'POST',
      body: JSON.stringify(blockData),
    });
  }

  async updateContentBlock(id, blockData) {
    return this.request(`/cms/content-blocks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(blockData),
    });
  }

  async deleteContentBlock(id) {
    return this.request(`/cms/content-blocks/${id}`, {
      method: 'DELETE',
    });
  }

  // License endpoints
  async getLicenses(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/licenses${queryParams ? `?${queryParams}` : ''}`);
  }

  async getLicenseById(id) {
    return this.request(`/licenses/${id}`);
  }

  async createLicense(licenseData) {
    return this.request('/licenses', {
      method: 'POST',
      body: JSON.stringify(licenseData),
    });
  }

  async updateLicense(id, licenseData) {
    return this.request(`/licenses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(licenseData),
    });
  }

  async deleteLicense(id) {
    return this.request(`/licenses/${id}`, {
      method: 'DELETE',
    });
  }

  async verifyLicense(licenseKey) {
    return this.request('/licenses/verify', {
      method: 'POST',
      body: JSON.stringify({ license_key: licenseKey }),
    });
  }

  async generateLicenseKey() {
    return this.request('/licenses/generate-key');
  }

  async getLicenseStatistics() {
    return this.request('/licenses/statistics');
  }

  async resendLicenseCertificateEmail(licenseId) {
    return this.request(`/licenses/${licenseId}/resend-email`, {
      method: 'POST',
    });
  }

  // License Tier endpoints
  async getLicenseTiers(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/license-tiers${queryParams ? `?${queryParams}` : ''}`);
  }

  async getPublicLicenseTiers() {
    return this.request('/license-tiers/public');
  }

  async getLicenseTierById(id) {
    return this.request(`/license-tiers/${id}`);
  }

  async createLicenseTier(tierData) {
    return this.request('/license-tiers', {
      method: 'POST',
      body: JSON.stringify(tierData),
    });
  }

  async updateLicenseTier(id, tierData) {
    return this.request(`/license-tiers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(tierData),
    });
  }

  async deleteLicenseTier(id) {
    return this.request(`/license-tiers/${id}`, {
      method: 'DELETE',
    });
  }

  async getChesiApps() {
    return this.request('/license-tiers/apps');
  }

  async setTierAppAccess(tierId, appId, isEnabled) {
    return this.request('/license-tiers/app-access', {
      method: 'POST',
      body: JSON.stringify({ tier_id: tierId, app_id: appId, is_enabled: isEnabled }),
    });
  }

  // Plan Selection endpoints
  async submitPlanSelection(data) {
    return this.request('/plan-selection/submit', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getPlanSelectionRequests(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/plan-selection${queryParams ? `?${queryParams}` : ''}`);
  }

  async getPendingPlanRequestsCount() {
    const response = await this.getPlanSelectionRequests({ status: 'pending' });
    if (response.success) {
      return response.data ? response.data.length : 0;
    }
    return 0;
  }

  async approvePlanRequest(id, data) {
    return this.request(`/plan-selection/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async rejectPlanRequest(id, data) {
    return this.request(`/plan-selection/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Support Ticket endpoints
  async getSupportTickets(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/support-tickets${queryParams ? `?${queryParams}` : ''}`);
  }

  async getSupportTicketById(id) {
    return this.request(`/support-tickets/${id}`);
  }

  async getSupportTicketsByLicense(licenseKey, filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/support-tickets/license/${licenseKey}${queryParams ? `?${queryParams}` : ''}`);
  }

  async createSupportTicket(ticketData, files = []) {
    const formData = new FormData();
    
    // Append form fields
    Object.keys(ticketData).forEach(key => {
      if (ticketData[key] !== undefined && ticketData[key] !== null) {
        formData.append(key, ticketData[key]);
      }
    });

    // Append files
    files.forEach(file => {
      formData.append('attachments', file);
    });

    const url = `${API_BASE_URL}/support-tickets`;
    const headers = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401) {
        this.setToken(null);
        window.location.href = '/login';
      }
      throw new Error(data.message || 'Request failed');
    }

    return data;
  }

  async updateSupportTicket(id, ticketData) {
    return this.request(`/support-tickets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(ticketData),
    });
  }

  async addTicketComment(id, comment, status, priority) {
    return this.request(`/support-tickets/${id}/comment`, {
      method: 'POST',
      body: JSON.stringify({ comment, status, priority }),
    });
  }

  async deleteSupportTicket(id) {
    return this.request(`/support-tickets/${id}`, {
      method: 'DELETE',
    });
  }

  async getSupportTicketStatistics() {
    return this.request('/support-tickets/statistics');
  }

  async downloadAttachment(attachmentId) {
    const url = `${API_BASE_URL}/support-tickets/${attachmentId}/download`;
    const headers = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: headers,
    });

    if (!response.ok) {
      throw new Error('Download failed');
    }

    return response.blob();
  }

  // Dashboard endpoints
  async getTopCountries() {
    return this.request('/dashboard/top-countries');
  }

  async getVisitorCount() {
    return this.request('/dashboard/visitor-count');
  }

  async getDashboardStats() {
    return this.request('/dashboard/stats');
  }

  // Reports endpoints
  async getAccountingReports(period = null, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (period) params.append('period', period);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/reports/accounting${params.toString() ? `?${params.toString()}` : ''}`);
  }

  async getRevenueBreakdown(period = null, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (period) params.append('period', period);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/reports/revenue-breakdown${params.toString() ? `?${params.toString()}` : ''}`);
  }
}

export default new ApiService();

